import * as React from "react";
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { CardHeader } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import {
  Tooltip as ChartTooltip,
  Area,
  AreaChart,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Stack } from "../../../../components/ui/stack";
import { LoadingIcon } from "../../../../components/system/loading-icon";
import { Alert } from "../../../../components/ui/alert";
import prettyBytes from "pretty-bytes";
import isObject from "lodash/isObject";
import { SamplingMethod, sampleMetrics } from "../sampleMetrics";
import { GroupedMetric } from "../../../../crud/metrics/types";
import { MoveVertical, ZoomIn, ZoomOut, Expand, Inbox } from "lucide-react";
import { TimeRange } from "../../types";
import { GroupedTooltip } from "./GroupedTooltip";
import { Domain } from "./types";
import { YAXIS_WIDTH } from "./const";
import { useTimezone } from "../../../../utils/hooks";
import { formatInTimeZone } from "date-fns-tz";

export type ZoomableAreaChartProps = {
  title?: string | ReactElement;
  data?: GroupedMetric[];
  maxValue?: number;
  isLoading: boolean;
  isError: boolean;
  samplingMethod?: SamplingMethod;
  domains: Domain[];
  yAxis?: ReactElement;
  xAxis?: ReactElement;
  tooltip?: ReactElement;
  legend?: ReactElement;
  cartesianGrid?: ReactElement;
  Chart?: any;
  Cartesian?: any;
  height?: number;
  timeRange?: TimeRange;
  setTimeRange: (timeRange?: TimeRange) => void;
  defaultTimeRange?: TimeRange;
  refAreaLeft: number;
  refAreaRight: number;
  setRefAreaLeft: (refAreaLeft: number) => void;
  setRefAreaRight: (refAreaRight: number) => void;
  clusterIsRunning?: boolean;
  emptyDataMessage?: string | ReactNode;
};

/**
 * A zoomable area chart that uses an array of type GroupedMetric[] as its data source.
 */
export const ZoomableAreaChart = ({
  title,
  data,
  isLoading,
  isError,
  samplingMethod,
  domains,
  yAxis,
  xAxis,
  tooltip,
  legend,
  cartesianGrid,
  Chart,
  Cartesian,
  height,
  timeRange,
  setTimeRange,
  defaultTimeRange,
  refAreaLeft,
  refAreaRight,
  setRefAreaLeft,
  setRefAreaRight,
  emptyDataMessage,
}: ZoomableAreaChartProps): ReactElement => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [displayTz] = useTimezone();

  const zoom = useCallback(() => {
    let _refAreaLeft = refAreaLeft;
    let _refAreaRight = refAreaRight;

    setRefAreaLeft(0);
    setRefAreaRight(0);

    if (!_refAreaLeft || !_refAreaRight) return;

    // Don't allow zooming into single datapoint
    if (_refAreaLeft === _refAreaRight) return;

    // Swap left and right if zooming right to left
    if (_refAreaLeft > _refAreaRight)
      [_refAreaLeft, _refAreaRight] = [_refAreaRight, _refAreaLeft];

    setTimeRange([_refAreaLeft, _refAreaRight]);
  }, [
    setTimeRange,
    setRefAreaLeft,
    setRefAreaRight,
    refAreaLeft,
    refAreaRight,
  ]);

  const getXAxis = () =>
    xAxis ? (
      xAxis
    ) : (
      <XAxis
        minTickGap={50}
        dataKey={(obj) => (obj ? obj.timestamp * 1000 : null)}
        type="number"
        scale="time"
        domain={timeRange ? timeRange : ["dataMin", "dataMax"]}
        tickFormatter={(v, i) =>
          formatInTimeZone(new Date(v), displayTz, "HH:mm:ss")
        }
        allowDuplicatedCategory={false}
        allowDataOverflow
      />
    );

  const getTooltip = () =>
    tooltip ? (
      tooltip
    ) : (
      <ChartTooltip
        content={
          <GroupedTooltip
            formatter={(value, name: string) => {
              const formattedVal = Array.isArray(value)
                ? value
                    .map((v) => prettyBytes(parseInt(v as string, 10)))
                    .join("–")
                : prettyBytes(parseInt(value as string, 10));
              const formattedName = name.replace("-", "–");
              return [formattedVal, formattedName];
            }}
            labelFormatter={(v) =>
              formatInTimeZone(new Date(v), displayTz, "LLL do, HH:mm:ss O")
            }
          />
        }
        wrapperStyle={{
          outline: "none",
          zIndex: 3, // Making sure the task prefix tooltip doesn't cover the other tooltips
        }}
        isAnimationActive={false}
      />
    );

  const getLegend = () =>
    legend ? (
      legend
    ) : (
      <Legend
        iconType="circle"
        iconSize={11}
        formatter={(value) => <span style={{ color: "#908e89" }}>{value}</span>}
      />
    );

  const getCartesianGrid = () =>
    cartesianGrid ? cartesianGrid : <CartesianGrid stroke="#eee" />;

  const ChartComponent = Chart || AreaChart;
  const CartesianComponent = Cartesian || Area;

  const hasSingleMetric = data?.length === 1;

  const isBarChart = ChartComponent === BarChart;

  const maxSamples = 500; // Max number of samples to show on the chart
  const { data: sampledData, maxValue } = useMemo(
    () =>
      sampleMetrics(data, timeRange, maxSamples, samplingMethod, isBarChart),
    [data, timeRange, samplingMethod, isBarChart],
  );

  const getYAxis = () =>
    yAxis ? (
      yAxis
    ) : (
      <YAxis
        tickFormatter={(value: number) =>
          Number.isFinite(value) ? prettyBytes(value) : "0 B"
        }
        domain={[0, maxValue]}
        width={YAXIS_WIDTH}
      />
    );

  // Track mouseup outside of the chart so we can drag and end selection outside chart
  useEffect(() => {
    const listener = (e: MouseEvent) => {
      // (#1570) Don't trigger the drag-outside-of-metrics-chart when clicking on the computation listing
      const computationListingEl = document.getElementById(
        "computation-listing",
      );
      if (computationListingEl?.contains(e.target as Node)) return;

      // If we stop propagation of the event, this prevents text selection when you start dragging in chart
      // but keep dragging outside of chart. But it also breaks column move/resize on AG Chart, so for now
      // it's better to just let events propagate.
      // e.stopImmediatePropagation();
      zoom();
    };

    document.addEventListener("mouseup", listener);

    return () => {
      document.removeEventListener("mouseup", listener);
    };
  }, [refAreaLeft, refAreaRight, zoom]);

  const maybeSnapToEdge = useCallback(() => {
    const rect = boxRef.current?.getBoundingClientRect();

    const listener = (e: MouseEvent) => {
      e.stopImmediatePropagation();

      if (!rect || !timeRange) return;

      const mouseExitedToRight = e.clientX > rect.left + rect.width;
      const mouseExitedToLeft = e.clientX < rect.left + YAXIS_WIDTH;

      if (mouseExitedToRight) {
        setRefAreaRight(timeRange[1]);
      } else if (mouseExitedToLeft) {
        setRefAreaRight(timeRange[0]);
      }

      document.removeEventListener("mousemove", listener);
    };

    document.addEventListener("mousemove", listener);
  }, [timeRange, setRefAreaRight]);

  // Snap to edges when mouse leaves the chart to the sides
  const onMouseLeave = useCallback(() => {
    maybeSnapToEdge();
  }, [maybeSnapToEdge]);

  const onMouseDown = useCallback(
    (e: { activeLabel: number }) => {
      setRefAreaLeft(e?.activeLabel);
    },
    [setRefAreaLeft],
  );

  const onMouseMove = useCallback(
    (e: any) => {
      // We also want to snap to edge when hovering over yaxis
      if (!e.chartX) {
        maybeSnapToEdge();
      }

      if (refAreaLeft && e?.activeLabel) {
        setRefAreaRight(e?.activeLabel);
      }
    },
    [maybeSnapToEdge, refAreaLeft, setRefAreaRight],
  );

  const hasZoomed = timeRange !== defaultTimeRange;

  return (
    <div
      className="group"
      style={
        {
          "--chart-button-opacity": "0",
        } as React.CSSProperties
      }
    >
      {title && (
        <CardHeader className="pl-0 pt-0">
          <ChartTitle
            title={title}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            isMaxZoom={sampledData?.length < 2}
          />
        </CardHeader>
      )}
      <div
        ref={boxRef}
        className="flex justify-center items-center relative select-none"
        style={{
          height: `${height || 200}px`,
        }}
      >
        {isLoading ? (
          <LoadingIcon />
        ) : isError ? (
          <Alert variant="destructive" className="mb-1">
            {`Error fetching ${title} data.`}
          </Alert>
        ) : sampledData?.length ? (
          <ResponsiveContainer width="100%" height={height || 200}>
            <ChartComponent
              data={sampledData || []}
              syncId="syncEverythingWithThisId"
              syncMethod="value"
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
              barCategoryGap={0}
              onMouseLeave={onMouseLeave}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              style={{ cursor: "ew-resize", userSelect: "none" }}
              {...(hasSingleMetric && isBarChart && { barSize: 20 })}
            >
              {getLegend()}
              {getCartesianGrid()}
              {getTooltip()}
              {getYAxis()}
              {getXAxis()}
              {domains
                .filter(({ color }) => !!color)
                .map(({ name, color, stack }) => (
                  <CartesianComponent
                    key={name}
                    type="monotone"
                    isAnimationActive={false}
                    dataKey={name}
                    stackId={stack}
                    stroke={isObject(color) ? color.stroke : color}
                    fill={isObject(color) ? color.fill : color}
                    fillOpacity={
                      isObject(color) && color.opacity != null
                        ? color.opacity
                        : 0.5
                    }
                  />
                ))}
              {refAreaLeft && refAreaRight && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                  strokeOpacity={0.3}
                />
              )}
            </ChartComponent>
          </ResponsiveContainer>
        ) : (
          <Stack justifyContent="center" className="w-full h-full bg-gray-100">
            {hasZoomed ? null : (
              <Stack
                alignItems="center"
                justifyContent="center"
                className="text-gray-700 opacity-50"
              >
                <Inbox />
                {emptyDataMessage ? emptyDataMessage : "No Data"}
              </Stack>
            )}
          </Stack>
        )}
      </div>
    </div>
  );
};

const ChartTitle = ({
  title,
  timeRange,
  setTimeRange,
  isMaxZoom,
}: {
  title: string | ReactElement;
  timeRange?: TimeRange;
  setTimeRange: (timeRange?: TimeRange) => void;
  isMaxZoom: boolean;
}): React.ReactElement => {
  const zoomIn = useCallback(() => {
    if (!timeRange) return;
    const start = timeRange[0] + (timeRange[1] - timeRange[0]) / 4;
    const end = timeRange[1] - (timeRange[1] - timeRange[0]) / 4;
    setTimeRange([start, end]);
  }, [timeRange, setTimeRange]);

  const zoomOut = useCallback(() => {
    if (!timeRange) return;
    const start = timeRange[0] - (timeRange[1] - timeRange[0]) / 2;
    const end = timeRange[1] + (timeRange[1] - timeRange[0]) / 2;
    setTimeRange([start, end]);
  }, [timeRange, setTimeRange]);

  const resetZoom = useCallback(() => {
    setTimeRange(undefined);
  }, [setTimeRange]);

  return (
    <div className="flex items-top justify-between mb-2">
      <div className="flex-1">{title}</div>
      <div className="flex-shrink-0">
        <div className="flex items-center space-x-1">
          <div className="flex items-center ml-3">
            <span className="text-sm text-gray-600 leading-8">
              <MoveVertical className="inline-block align-middle transform rotate-90 w-4 h-4" />{" "}
              Drag to zoom
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <TopRightButton
              onClick={zoomIn}
              Icon={ZoomIn}
              disabled={isMaxZoom}
            />
            <TopRightButton onClick={zoomOut} Icon={ZoomOut} />
            <Tooltip>
              <TooltipTrigger>
                <TopRightButton onClick={resetZoom} Icon={Expand} />
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset Zoom</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

type TopRightButtonType = {
  Icon: React.ComponentType<{ className?: string }>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const TopRightButton = React.forwardRef<HTMLButtonElement, TopRightButtonType>(
  (props, ref) => {
    const { Icon, ...rest } = props;
    return (
      <Button
        variant="ghost"
        size="icon"
        className="min-w-auto"
        {...rest}
        ref={ref}
      >
        <Icon className="w-4 h-4" />
      </Button>
    );
  },
);
